import { Fragment, useContext, useMemo } from "react";
import styled from "styled-components";
import { PrimaryButton } from "../../../../components/button";
import { StyledRadio } from "../../../../components/forms/radio";
import { columnFlex, Flex } from "../../../../components/layout";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import { VerificationDTO } from "../../../../store/verification/models/dto";
import { ServiceType } from "../../../../store/verification/models/internal";
import { colours } from "../../../../styles/colours";
import { SingleKycContext } from "./context";

const Container = styled.div<{ right?: boolean; shrink?: boolean }>`
  overflow: auto;
  flex-grow: 1;
  flex-shrink: ${({ shrink }) => (shrink ? "1" : "0")};
  ${({ right }) => (right ? "text-align: right;" : "")}
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const Wrapper = styled.form`
  ${columnFlex};
  min-width: 25em;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;

  tr:nth-child(even) {
    background-color: ${colours.lightestBlue};
  }
  tr.selected {
    background-color: ${colours.redFailed};
  }

  td {
    padding: 0.5em;

    &:not(:first-child) {
      text-align: center;
    }
  }
`;

const Buttons = styled(Flex)`
  justify-content: flex-end;
`;

const CTA = styled(PrimaryButton)`
  & + & {
    margin-left: 1em;
  }
`;

export interface IApprovalModalProps {
  service?: typeof ServiceType.T;
}

export function ApprovalModal({ service }: IApprovalModalProps) {
  const { setOverlay } = useContext(GlobalOverlayContext);

  const {
    originalDeclineCodes,
    newDeclineCodes,
    addDeclineCode,
    removeDeclineCode,
    allDeclineCodes,
  } = useContext(SingleKycContext);

  const codes = useMemo(
    () =>
      allDeclineCodes.fold(
        () => null,
        (codes) =>
          codes
            .filter(({ service: s }) => s === service)
            .map(({ service, declineCodes }) => {
              return declineCodes.length > 0 ? (
                <Fragment key={service}>
                  <tr>
                    <td></td>
                    <td>Verify</td>
                    <td>Decline</td>
                  </tr>
                  {declineCodes
                    .sort((a) =>
                      originalDeclineCodes[service]?.includes(a.code) ? -1 : 1
                    )
                    .map(({ code, message }) => (
                      <tr
                        key={code}
                        className={
                          !!newDeclineCodes[service]?.includes(code)
                            ? "selected"
                            : ""
                        }
                      >
                        <td>{message}</td>
                        <td>
                          <StyledRadio
                            onClick={() => removeDeclineCode(code, service)}
                            checked={!newDeclineCodes[service]?.includes(code)}
                          />
                        </td>
                        <td>
                          <StyledRadio
                            onClick={() => addDeclineCode(code, service)}
                            checked={!!newDeclineCodes[service]?.includes(code)}
                          />
                        </td>
                      </tr>
                    ))}
                </Fragment>
              ) : null;
            })
      ),
    [
      allDeclineCodes,
      removeDeclineCode,
      addDeclineCode,
      originalDeclineCodes,
      newDeclineCodes,
    ]
  );

  return (
    <Wrapper>
      <Container shrink>
        <Table>{codes}</Table>
      </Container>
      <Container>
        <Buttons>
          <CTA
            variant="outlined"
            onClick={() =>
              setOverlay({
                title: "",
                children: null,
              })
            }
          >
            Close
          </CTA>
        </Buttons>
      </Container>
    </Wrapper>
  );
}
